import React, { useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import AppLayout from '../../components/AppLayout';
import { COCKPIT_CSS } from '../cockpit/cockpitStyles';
import { CRM_CSS } from './crmStyles';
import { useCockpitDb } from '../cockpit/CockpitDataContext';
import {
  TEAM_STATUS, STATUS_ORDER, statusOf, TYPES_ACTION, RELANCE_DAYS,
  comptes, equipesOfCompte, toRelance, daysSinceContact,
  initials, fullName,
} from './crmData';

// ─────────────────────────────────────────────────────────────────────────────
// Reporting — la vue de pilotage, tous comptes confondus : qui n'a pas été
// touché depuis trop longtemps, quelles actions ont été menées, et où en est
// la couverture des équipes.
// ─────────────────────────────────────────────────────────────────────────────

// Rang de tri des relances : jamais contacté d'abord, puis du plus ancien
// contact au plus récent.
const rank = (d) => (d === null ? Number.MAX_SAFE_INTEGER : d);

export default function Reporting() {
  const { db } = useCockpitDb();
  const navigate = useNavigate();

  const list = comptes(db);
  const fiches = (db && db.fiches) || [];

  const equipes = useMemo(() => comptes(db).flatMap((c) => equipesOfCompte(c)), [db]);

  const relance = useMemo(() => {
    if (!db) return [];
    return toRelance(db)
      .map((f) => ({ f, d: daysSinceContact(db, f.id) }))
      .sort((a, b) => rank(b.d) - rank(a.d));
  }, [db]);

  const parType = useMemo(() => {
    const acts = (db && db.actions) || [];
    return TYPES_ACTION.map((k) => ({ k, label: k, n: acts.filter((a) => a.type === k).length }));
  }, [db]);

  const parStatut = useMemo(
    () => STATUS_ORDER.map((k) => ({
      k,
      label: TEAM_STATUS[k].label,
      color: TEAM_STATUS[k].color,
      n: equipes.filter(({ eq }) => statusOf(eq) === k).length,
    })),
    [equipes]
  );

  if (!db) return null;

  const nbActions = (db.actions || []).length;
  const actives = parStatut.find((s) => s.k === 'actif');

  // Aucune donnée du tout : le reporting n'a rien à agréger.
  if (!list.length && !fiches.length) {
    return (
      <AppLayout title="CRM · Reporting">
        <style>{COCKPIT_CSS}</style>
        <style>{CRM_CSS}</style>
        <div className="ckpt">
          <div className="ckpt-empty">
            <div className="ic">📊</div>
            <div className="t">Rien à reporter pour l'instant</div>
            <div className="d">Le reporting agrège vos comptes, vos fiches clients et les actions menées. Commencez par créer un compte et y référencer vos contacts.</div>
            <button className="btn btn-primary" onClick={() => navigate('/crm/comptes')}>
              + Compte client
            </button>
          </div>
        </div>
      </AppLayout>
    );
  }

  // Barres horizontales : largeur proportionnelle au plus haut compteur.
  const bars = (rows, color) => {
    const max = Math.max(1, ...rows.map((r) => r.n));
    return (
      <div className="crm-bars">
        {rows.map((r) => (
          <div className="crm-bar" key={r.k}>
            <span className="crm-bar-k">{r.label}</span>
            <span className="crm-bar-t">
              <span className="crm-bar-f" style={{ width: `${(r.n / max) * 100}%`, background: r.color || color }} />
            </span>
            <span className="crm-bar-v">{r.n}</span>
          </div>
        ))}
      </div>
    );
  };

  return (
    <AppLayout title="CRM · Reporting">
      <style>{COCKPIT_CSS}</style>
      <style>{CRM_CSS}</style>
      <div className="ckpt">

        <div className="kpis">
          <div className="kpi">
            <div className="lbl">Fiches clients</div>
            <div className="val">{fiches.length}</div>
            <div className="chip">contacts référencés</div>
          </div>
          <div className="kpi">
            <div className="lbl">Comptes</div>
            <div className="val">{list.length}</div>
            <div className="chip">{equipes.length} équipe(s) cartographiée(s)</div>
          </div>
          <div className="kpi">
            <div className="lbl">Équipes actives</div>
            <div className="val good">{actives ? actives.n : 0}</div>
            <div className="chip">missions en cours</div>
          </div>
          <div className="kpi">
            <div className="lbl">À relancer</div>
            <div className={relance.length ? 'val alert' : 'val'}>{relance.length}</div>
            <div className="chip">seuil : {RELANCE_DAYS} jours</div>
          </div>
        </div>

        {/* ── Clients à relancer ── */}
        <div className="acc-sec-head">
          <div className="t">Clients à relancer</div>
          <div className="c">Jamais contactés, ou sans contact depuis {RELANCE_DAYS} jours</div>
        </div>
        <div className="card crm-card" style={{ marginBottom: 18 }}>
          {relance.length ? (
            <div className="crm-rl">
              {relance.map(({ f, d }) => (
                <button className="crm-rl-row" key={f.id} onClick={() => navigate(`/crm/fiches/${f.id}`)}>
                  <span className="crm-av">{initials(fullName(f))}</span>
                  <span className="crm-rl-id">
                    <span className="crm-rl-nm">
                      {fullName(f)}
                      {f.fonction && <span className="crm-fiche-fn"> · {f.fonction}</span>}
                    </span>
                    <span className="crm-rl-sub">
                      {[f.team, f.entity, f.account].filter(Boolean).join(' · ') || 'Non rattachée'}
                    </span>
                  </span>
                  <span className="sp" />
                  <span className="crm-pill crm-pill-warn">
                    {d === null ? 'Jamais contacté' : `il y a ${d} j`}
                  </span>
                </button>
              ))}
            </div>
          ) : (
            <div className="crm-team-empty">
              {fiches.length
                ? 'Aucune relance en attente — tous vos contacts ont été touchés récemment.'
                : 'Aucune fiche client enregistrée pour l’instant.'}
            </div>
          )}
        </div>

        {/* ── Actions par type ── */}
        <div className="acc-sec-head">
          <div className="t">Actions par type</div>
          <div className="c">{nbActions} action(s) journalisée(s)</div>
        </div>
        <div className="card crm-card" style={{ marginBottom: 18 }}>
          {nbActions
            ? bars(parType)
            : <div className="crm-team-empty">Aucune action journalisée — enregistrez vos appels, mails et RDV depuis les fiches clients.</div>}
        </div>

        {/* ── Répartition des équipes par statut ── */}
        <div className="acc-sec-head">
          <div className="t">Répartition des équipes par statut</div>
          <div className="c">{equipes.length} équipe(s) sur {list.length} compte(s)</div>
        </div>
        <div className="card crm-card">
          {equipes.length
            ? bars(parStatut)
            : <div className="crm-team-empty">Aucune équipe cartographiée — décrivez le périmètre de vos comptes pour alimenter cette vue.</div>}
        </div>

      </div>
    </AppLayout>
  );
}
